/* eslint-disable no-nested-ternary */
import React, { useMemo, useState } from 'react';
import { Spin, Alert } from 'antd';
import useChangelog from './useChangelog';

const STYLES = `
.cl-root {
  --cl-bg: #F7F8FB;
  --cl-card: #FFFFFF;
  --cl-line: #E4E8EF;
  --cl-line-strong: #CBD3DF;
  --cl-text: #0F172A;
  --cl-text-soft: #5B6878;
  --cl-text-mute: #94A0B0;
  --cl-accent: #25547A;
  --cl-accent-bright: #2F86C4;
  --cl-accent-soft: rgba(47, 134, 196, 0.08);
  --cl-glow: rgba(47, 134, 196, 0.32);

  min-height: 100%;
  background: var(--cl-bg);
  padding: 56px 24px 96px;
  color: var(--cl-text);
  font-feature-settings: "ss01", "cv11";
}
.cl-wrap {
  max-width: 860px;
  margin: 0 auto;
}
.cl-header {
  margin-bottom: 56px;
  animation: cl-fade-down 0.5s ease-out both;
}
.cl-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--cl-accent);
  margin-bottom: 14px;
}
.cl-eyebrow::before {
  content: '';
  display: inline-block;
  width: 22px; height: 1px;
  background: var(--cl-accent);
}
.cl-title {
  font-size: 46px;
  font-weight: 700;
  letter-spacing: -0.028em;
  line-height: 1.05;
  margin: 0;
  color: var(--cl-text);
}
.cl-subtitle {
  margin-top: 18px;
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 14px;
  color: var(--cl-text-soft);
}
.cl-subtitle b {
  color: var(--cl-text);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.cl-subtitle .cl-sep {
  width: 4px; height: 4px;
  border-radius: 50%;
  background: var(--cl-text-mute);
}

.cl-timeline {
  position: relative;
  padding-left: 42px;
}
.cl-timeline::before {
  content: '';
  position: absolute;
  left: 11px; top: 10px; bottom: 10px;
  width: 1px;
  background: linear-gradient(
    180deg,
    var(--cl-line-strong) 0%,
    var(--cl-line) 70%,
    transparent 100%
  );
}

.cl-item {
  position: relative;
  margin-bottom: 28px;
  animation: cl-fade-up 0.5s cubic-bezier(.2,.7,.2,1) both;
}
.cl-item:last-child { margin-bottom: 0; }

.cl-dot {
  position: absolute;
  left: -36px;
  top: 26px;
  width: 14px; height: 14px;
  border-radius: 50%;
  background: var(--cl-card);
  border: 2px solid var(--cl-line-strong);
  z-index: 1;
  transition: transform 0.2s ease;
}
.cl-dot--latest {
  background: var(--cl-accent-bright);
  border-color: var(--cl-accent-bright);
  animation: cl-pulse 2.4s ease-in-out infinite;
}

.cl-card {
  position: relative;
  background: var(--cl-card);
  border: 1px solid var(--cl-line);
  border-radius: 14px;
  padding: 22px 26px 18px;
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s ease;
}
.cl-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 30px -18px rgba(15, 23, 42, 0.22);
  border-color: var(--cl-line-strong);
}
.cl-card--latest {
  border-color: rgba(47, 134, 196, 0.38);
  background:
    linear-gradient(180deg, #FFFFFF 0%, rgba(47, 134, 196, 0.03) 100%);
  box-shadow: 0 10px 28px -18px var(--cl-glow);
}
.cl-card--latest:hover {
  box-shadow: 0 18px 36px -18px var(--cl-glow);
}

.cl-card-head {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.cl-version {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.018em;
  font-variant-numeric: tabular-nums;
  color: var(--cl-text);
}
.cl-date {
  font-size: 13px;
  color: var(--cl-text-soft);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}
.cl-pill {
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cl-accent-bright);
  background: var(--cl-accent-soft);
  padding: 5px 11px;
  border-radius: 999px;
  white-space: nowrap;
}
.cl-bserver {
  margin: -8px 0 14px 0;
  font-size: 13px;
  color: var(--cl-text-soft);
  font-variant-numeric: tabular-nums;
}
.cl-bserver b {
  color: var(--cl-text);
  font-weight: 600;
}
.cl-card--latest .cl-bserver {
  color: var(--cl-accent);
}
.cl-card--latest .cl-bserver b {
  color: var(--cl-accent);
}

.cl-entries {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cl-entry {
  display: flex;
  gap: 14px;
  padding: 11px 0;
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--cl-text);
  border-top: 1px solid var(--cl-line);
}
.cl-entry:first-child { border-top: none; padding-top: 4px; }
.cl-entry-marker {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 2px;
  margin-top: 9px;
  background: var(--cl-text-mute);
}
.cl-card--latest .cl-entry-marker { background: var(--cl-accent-bright); }

.cl-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}
.cl-state-icon {
  width: 56px; height: 56px;
  border-radius: 50%;
  background: var(--cl-accent-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}
.cl-state-icon::after {
  content: '';
  width: 18px; height: 18px;
  border-radius: 50%;
  border: 2px solid var(--cl-accent-bright);
  border-top-color: transparent;
  opacity: 0.8;
}
.cl-state-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--cl-text);
  letter-spacing: -0.015em;
}
.cl-state-sub {
  margin-top: 8px;
  font-size: 14px;
  color: var(--cl-text-soft);
  max-width: 380px;
}
.cl-state-sub button {
  margin-top: 14px;
  background: transparent;
  border: 1px solid var(--cl-line-strong);
  color: var(--cl-text);
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.cl-state-sub button:hover {
  background: var(--cl-accent-soft);
  border-color: var(--cl-accent-bright);
  color: var(--cl-accent-bright);
}

.cl-search {
  margin-top: 28px;
  position: relative;
  max-width: 460px;
}
.cl-search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: 1.5px solid var(--cl-text-mute);
  border-radius: 50%;
  pointer-events: none;
  transition: border-color 0.18s ease;
}
.cl-search-icon::after {
  content: '';
  position: absolute;
  width: 6px;
  height: 1.5px;
  background: var(--cl-text-mute);
  bottom: -3px;
  right: -4px;
  transform: rotate(45deg);
  border-radius: 1px;
  transition: background 0.18s ease;
}
.cl-search-input {
  width: 100%;
  height: 44px;
  background: var(--cl-card);
  border: 1px solid var(--cl-line);
  border-radius: 12px;
  padding: 0 44px 0 44px;
  font-size: 14.5px;
  color: var(--cl-text);
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  font-family: inherit;
}
.cl-search-input::placeholder {
  color: var(--cl-text-mute);
}
.cl-search-input:focus {
  border-color: var(--cl-accent-bright);
  box-shadow: 0 0 0 4px var(--cl-accent-soft);
}
.cl-search:focus-within .cl-search-icon,
.cl-search:focus-within .cl-search-icon::after {
  border-color: var(--cl-accent-bright);
  background: var(--cl-accent-bright);
}
.cl-search:focus-within .cl-search-icon { background: transparent; }
.cl-search-clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  background: var(--cl-line);
  color: var(--cl-text-soft);
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
}
.cl-search-clear:hover {
  background: var(--cl-text-soft);
  color: var(--cl-card);
}

.cl-mark {
  background: rgba(47, 134, 196, 0.18);
  color: var(--cl-accent);
  padding: 1px 3px;
  margin: 0 -1px;
  border-radius: 3px;
  font-weight: 600;
}
.cl-card--latest .cl-mark {
  background: rgba(47, 134, 196, 0.22);
}

@keyframes cl-fade-up {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes cl-fade-down {
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes cl-pulse {
  0%, 100% { box-shadow: 0 0 0 0 var(--cl-glow); }
  50%      { box-shadow: 0 0 0 8px rgba(47, 134, 196, 0); }
}

@media (max-width: 640px) {
  .cl-root { padding: 36px 16px 64px; }
  .cl-title { font-size: 36px; }
  .cl-timeline { padding-left: 32px; }
  .cl-dot { left: -28px; }
  .cl-card { padding: 18px 18px 14px; }
}
`;

function pluralize(n, forms) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlight(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  const lower = query.toLowerCase();
  return parts.map((part, i) => (
    part.toLowerCase() === lower
      // eslint-disable-next-line react/no-array-index-key
      ? <mark key={i} className="cl-mark">{part}</mark>
      // eslint-disable-next-line react/no-array-index-key
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));
}

function ChangeList() {
  const { versions, loading, error } = useChangelog();
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const normalizedQuery = trimmedQuery.toLowerCase();

  const filteredVersions = useMemo(() => {
    if (!normalizedQuery) return versions;
    return versions.flatMap((v) => {
      const matchesVersion = v.version.toLowerCase().includes(normalizedQuery);
      const matchingEntries = v.entries.filter(
        (e) => e.toLowerCase().includes(normalizedQuery),
      );
      if (matchesVersion) return [{ ...v, entries: v.entries }];
      if (matchingEntries.length > 0) return [{ ...v, entries: matchingEntries }];
      return [];
    });
  }, [versions, normalizedQuery]);

  const latestVersion = versions[0]?.version;

  if (loading) {
    return (
      <div className="cl-root">
        <style>{STYLES}</style>
        <div className="cl-state">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cl-root">
        <style>{STYLES}</style>
        <div className="cl-wrap">
          <Alert
            type="error"
            message="Не удалось загрузить changelog"
            description={error}
            showIcon
          />
        </div>
      </div>
    );
  }

  return (
    <div className="cl-root">
      <style>{STYLES}</style>
      <div className="cl-wrap">
        <header className="cl-header">
          <div className="cl-eyebrow">Changelog</div>
          <h1 className="cl-title">История версий</h1>
          {versions.length > 0 ? (
            <div className="cl-subtitle">
              {trimmedQuery ? (
                <span>
                  <b>{filteredVersions.length}</b>
                  {` ${pluralize(filteredVersions.length, ['версия', 'версии', 'версий'])} по запросу «${trimmedQuery}»`}
                </span>
              ) : (
                <>
                  <span>
                    <b>{versions.length}</b>
                    {` ${pluralize(versions.length, ['релиз', 'релиза', 'релизов'])}`}
                  </span>
                  <span className="cl-sep" />
                  <span>
                    {'последний — '}
                    <b>{versions[0].date}</b>
                  </span>
                </>
              )}
            </div>
          ) : (
            <div className="cl-subtitle">
              <span>Релизов пока нет</span>
            </div>
          )}

          {versions.length > 0 && (
            <div className="cl-search">
              <span className="cl-search-icon" />
              <input
                type="text"
                className="cl-search-input"
                placeholder="Поиск по записям…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button
                  type="button"
                  className="cl-search-clear"
                  onClick={() => setQuery('')}
                  aria-label="Очистить поиск"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </header>

        {versions.length === 0 ? (
          <div className="cl-state">
            <div className="cl-state-icon" />
            <div className="cl-state-title">Здесь будет история релизов</div>
            <div className="cl-state-sub">
              После первого деплоя в этом разделе появятся версии и список изменений.
            </div>
          </div>
        ) : filteredVersions.length === 0 ? (
          <div className="cl-state">
            <div className="cl-state-icon" />
            <div className="cl-state-title">Ничего не найдено</div>
            <div className="cl-state-sub">
              {`По запросу «${trimmedQuery}» нет совпадений.`}
              <br />
              <button type="button" onClick={() => setQuery('')}>Сбросить поиск</button>
            </div>
          </div>
        ) : (
          <div className="cl-timeline">
            {filteredVersions.map((v, idx) => {
              const isLatest = v.version === latestVersion;
              return (
                <article
                  key={v.version}
                  className="cl-item"
                  style={{ animationDelay: `${0.06 * idx}s` }}
                >
                  <span className={`cl-dot${isLatest ? ' cl-dot--latest' : ''}`} />
                  <div className={`cl-card${isLatest ? ' cl-card--latest' : ''}`}>
                    <div className="cl-card-head">
                      <span className="cl-version">{highlight(`v${v.version}`, trimmedQuery)}</span>
                      <span className="cl-date">{v.date}</span>
                      {isLatest && <span className="cl-pill">Текущая</span>}
                    </div>
                    {v.requirementVersion && (
                      <div className="cl-bserver" title={`Минимальная требуемая версия ${v.requirementLabel}`}>
                        {`${v.requirementLabel} ≥ `}
                        <b>{v.requirementVersion}</b>
                      </div>
                    )}
                    <ul className="cl-entries">
                      {v.entries.map((entry, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={`${v.version}-${i}`} className="cl-entry">
                          <span className="cl-entry-marker" />
                          <span>{highlight(entry, trimmedQuery)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChangeList;
